import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MoveHorizontal } from "lucide-react";
import { Reveal, EASE } from "../motion";
import { useLang } from "../../lib/i18n";

const REGROWTH = [90, 78, 65, 52, 40, 28, 18, 10, 4, 0];

const SKIN_TONES = [
  { hex: "#F7E6D4", label: "I" },
  { hex: "#F0D3B6", label: "II" },
  { hex: "#E2B78E", label: "III" },
  { hex: "#C68E5E", label: "IV" },
  { hex: "#8D5524", label: "V" },
  { hex: "#4A2C17", label: "VI" },
];

const HAIR_COLOR = "#221307";
const HAIR_COLOR_DARK = "#0D0602";

function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const HairField = ({ className = "", tone = 1 }) => {
  const stroke = tone >= 4 ? HAIR_COLOR_DARK : HAIR_COLOR;
  const boost = tone >= 4 ? 0.35 : 0;
  const hairs = useMemo(() => {
    const rng = mulberry32(20260825);
    const arr = [];
    for (let i = 0; i < 1600; i++) {
      const x = rng();
      const density = Math.pow(1 - x, 1.6);
      if (rng() < density * 0.98) {
        arr.push({
          x: x * 1000,
          y: 40 + rng() * 240,
          len: 16 + rng() * 26,
          tilt: (rng() - 0.5) * 14,
          w: 0.7 + rng() * 1.1,
          o: 0.45 + rng() * 0.55,
        });
      }
    }
    return arr;
  }, []);
  return (
    <svg viewBox="0 0 1000 320" preserveAspectRatio="none" className={className} aria-hidden="true">
      {hairs.map((h, i) => (
        <line
          key={i}
          x1={h.x}
          y1={h.y}
          x2={h.x + h.tilt}
          y2={h.y - h.len}
          stroke={stroke}
          strokeWidth={h.w}
          strokeLinecap="round"
          opacity={Math.min(1, h.o + boost)}
        />
      ))}
    </svg>
  );
};

const SkinTexture = ({ tone }) => (
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    <div
      className="absolute inset-0"
      style={{
        background: `radial-gradient(ellipse 60% 50% at 30% 35%, rgba(255,255,255,${tone <= 3 ? 0.14 : 0.06}) 0%, transparent 70%), radial-gradient(ellipse 50% 45% at 72% 68%, rgba(120,60,30,${tone <= 3 ? 0.08 : 0.12}) 0%, transparent 70%)`,
      }}
    />
  </div>
);

export const LaserJourney = () => {
  const { t } = useLang();
  const trackRef = useRef(null);
  const [pos, setPos] = useState(0.05);
  const [dragging, setDragging] = useState(false);
  const [tone, setTone] = useState(1);
  const session = Math.min(10, Math.floor(pos * 10) + 1);
  const regrowth = REGROWTH[session - 1];
  const phrases = t("journey.phrases");
  const M = 1.8;
  const R = 80;
  const skin = SKIN_TONES[tone].hex;

  const update = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    setPos(Math.min(0.999, Math.max(0.001, (clientX - rect.left) / rect.width)));
  };

  const onPointerDown = (e) => {
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };

  const W = trackRef.current?.getBoundingClientRect().width || 1000;
  const H = 320;

  return (
    <section id="resultats" className="py-28 lg:py-36 bg-white overflow-hidden" data-testid="laser-journey-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-[12px] tracking-[0.28em] uppercase text-taupe-dark mb-4">{t("journey.kicker")}</p>
          <h2 className="font-display tracking-tight text-4xl sm:text-5xl lg:text-6xl text-espresso max-w-3xl leading-tight">
            {t("journey.titleA")} <span className="italic text-taupe">{t("journey.titleB")}</span>
          </h2>
          <p className="mt-4 text-base text-espresso/65 font-light max-w-xl flex items-center gap-2">
            <MoveHorizontal className="w-4 h-4 text-taupe shrink-0" strokeWidth={1.5} />
            {t("journey.instruction")}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div
                ref={trackRef}
                data-testid="loupe-track"
                onPointerDown={onPointerDown}
                onPointerMove={(e) => dragging && update(e.clientX)}
                onPointerUp={() => setDragging(false)}
                onPointerCancel={() => setDragging(false)}
                className="relative h-[320px] rounded-3xl border border-taupe/25 overflow-hidden cursor-ew-resize touch-none select-none transition-colors duration-500"
                style={{ background: skin }}
              >
                <SkinTexture tone={tone} />
                <HairField className="absolute inset-0 w-full h-full opacity-40" tone={tone} />
                <div className="absolute inset-x-0 bottom-0 h-px bg-espresso/20" />
                {Array.from({ length: 10 }).map((_, i) => (
                  <span
                    key={i}
                    className="absolute bottom-2.5 text-[9px] tracking-[0.08em] uppercase -translate-x-1/2"
                    style={{ left: `${((i + 0.5) / 10) * 100}%`, color: tone >= 4 ? "rgba(249,246,240,0.55)" : "rgba(44,36,27,0.45)" }}
                  >
                    S{i + 1}
                  </span>
                ))}
                <div
                  data-testid="loupe"
                  className="absolute top-1/2 rounded-full overflow-hidden border-2 border-gold shadow-[0_20px_60px_rgba(44,36,27,0.35)] pointer-events-none"
                  style={{ width: R * 2, height: R * 2, left: `calc(${(pos * 100).toFixed(2)}% - ${R}px)`, marginTop: -R, background: skin }}
                >
                  <SkinTexture tone={tone} />
                  <div
                    className="absolute"
                    style={{
                      width: W * M,
                      height: H * M,
                      left: -(pos * W * M - R),
                      top: -(H * 0.5 * M - R),
                    }}
                  >
                    <HairField className="w-full h-full" tone={tone} />
                  </div>
                  <div className="absolute inset-0 rounded-full ring-1 ring-inset ring-white/40" />
                  <Search className="absolute bottom-4 right-5 w-5 h-5" strokeWidth={1.5} style={{ color: tone >= 4 ? "rgba(249,246,240,0.6)" : "rgba(44,36,27,0.5)" }} />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3" data-testid="phototype-picker">
                  <span className="text-[11px] tracking-[0.18em] uppercase text-espresso/55">{t("journey.skinTone")}</span>
                  <div className="flex items-center gap-2">
                    {SKIN_TONES.map((s, i) => (
                      <button
                        key={s.label}
                        data-testid={`phototype-${i}`}
                        onClick={() => setTone(i)}
                        aria-label={`Phototype ${s.label}`}
                        className={`w-8 h-8 rounded-full border transition-[transform,box-shadow] duration-300 hover:scale-110 ${
                          tone === i ? "ring-2 ring-gold ring-offset-2 ring-offset-white scale-110" : "border-espresso/15"
                        }`}
                        style={{ background: s.hex }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex-1 min-w-[180px] h-1.5 rounded-full bg-beige overflow-hidden">
                  <div
                    className="h-full bg-taupe rounded-full transition-[width] duration-200"
                    style={{ width: `${(session / 10) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={session}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="bg-espresso text-cream rounded-3xl p-8 lg:p-10"
                  data-testid="session-card"
                >
                  <p className="text-[11px] tracking-[0.24em] uppercase text-gold">{t("journey.sessionOf", { n: session })}</p>
                  <p className="font-display text-6xl lg:text-7xl mt-4" data-testid="regrowth-value">
                    {regrowth}<span className="text-3xl text-gold">%</span>
                  </p>
                  <p className="text-xs tracking-[0.18em] uppercase text-cream/55 mt-1">{t("journey.regrowthLabel")}</p>
                  <p className="mt-6 text-sm text-cream/75 font-light leading-relaxed" data-testid="session-phrase">
                    {phrases[session - 1]}
                  </p>
                </motion.div>
              </AnimatePresence>
              <p className="mt-5 text-xs text-espresso/45 font-light leading-relaxed">
                {t("journey.disclaimer")}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
